import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface AgingData {
  '2-5': number;
  '6-10': number;
  '11-15': number;
  '16-30': number;
  '>30': number;
}

interface AgingDistributionChartProps {
  data: AgingData;
  onBarClick?: (bucket: string) => void;
  title?: string;
}

const bucketLabels: Record<string, string> = {
  '2-5': '2-5 Days',
  '6-10': '6-10 Days',
  '11-15': '11-15 Days',
  '16-30': '16-30 Days',
  '>30': '>30 Days',
};

const bucketColors: Record<string, string> = {
  '2-5': 'rgba(59, 130, 246, 0.8)',      // blue
  '6-10': 'rgba(16, 185, 129, 0.8)',     // green
  '11-15': 'rgba(245, 158, 11, 0.8)',    // amber
  '16-30': 'rgba(249, 115, 22, 0.8)',    // orange
  '>30': 'rgba(239, 68, 68, 0.8)',       // red
};

const AgingDistributionChart: React.FC<AgingDistributionChartProps> = ({ data, onBarClick, title = 'Request Aging Distribution' }) => {
  const bucketKeys = Object.keys(bucketLabels);
  const labels = bucketKeys.map(key => bucketLabels[key]);
  const values = bucketKeys.map(key => data[key as keyof AgingData] || 0);
  const colors = bucketKeys.map(key => bucketColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Aging Tickets',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 50,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} tickets (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 14,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(bucketKeys[index]);
      }
    },
  };

  return (
    <div id="chart-aging-distribution" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">Click to filter</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {bucketKeys.map((bucket, index) => (
          <button
            key={bucket}
            onClick={() => onBarClick?.(bucket)}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: bucketColors[bucket], color: 'white' }}
          >
            {bucketLabels[bucket]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AgingDistributionChart;
